import { BAND, RAINBOW_ORDER } from "./sections";

// Painterly entrance: grey rain over hills, the sun breaking through, a rainbow seen in the rain and grounded on the land.
export const clamp01 = (x) => Math.max(0, Math.min(1, x));
export const ease = (x) => x * x * (3 - 2 * x);
const rand = (a, b) => a + Math.random() * (b - a);
const BANDS = RAINBOW_ORDER.map((z) => BAND[z]);

const canvas = (w, h) => {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
};

// Geometry shared by every layer, in CSS pixels.
export function geometry(W, H) {
  const hz = H * (W < H ? 0.6 : 0.58);
  const R = Math.min(W * (W < H ? 0.46 : 0.4), H * 0.4);
  return { W, H, hz, cx: W * 0.5, cy: hz + H * 0.035, R, bw: R * 0.07, sun: { x: W * (W < H ? 0.74 : 0.78), y: H * (W < H ? 0.17 : 0.16) } };
}

function hill(g, W, base, amp, seed, fill, shade) {
  const top = (x) => base - amp * (0.5 + 0.5 * Math.sin((x / W) * 4.2 + seed) * Math.cos((x / W) * 1.7 + seed * 1.7));
  const shape = (dy) => {
    g.beginPath();
    g.moveTo(-20, top(-20) + dy);
    for (let x = -20; x <= W + 20; x += W / 64) g.lineTo(x, top(x) + dy);
    g.lineTo(W + 20, base + amp * 8);
    g.lineTo(-20, base + amp * 8);
  };
  g.fillStyle = fill;
  g.globalAlpha = 0.35;
  shape(-2.5);
  g.fill();
  g.globalAlpha = 1;
  shape(0);
  const gr = g.createLinearGradient(0, base - amp, 0, base + amp * 2);
  gr.addColorStop(0, fill);
  gr.addColorStop(1, shade);
  g.fillStyle = gr;
  g.fill();
}

// The land is drawn after the rainbow, so the rainbow's feet sink into the hills.
export function paintLand(G, dpr) {
  const { W, H, hz, cx } = G;
  const c = canvas(W * dpr, H * dpr);
  const g = c.getContext("2d");
  g.scale(dpr, dpr);
  hill(g, W, hz - H * 0.005, H * 0.07, 0.6, "#BDB6D6", "#ABB4C8");
  hill(g, W, hz + H * 0.03, H * 0.055, 2.1, "#A7C2B4", "#9DBB9E");
  hill(g, W, hz + H * 0.055, H * 0.04, 4.2, "#B3CF9F", "#B9D6A2");
  const feet = g.createLinearGradient(0, hz - H * 0.03, 0, hz + H * 0.06);
  feet.addColorStop(0, "rgba(255,246,248,0)");
  feet.addColorStop(0.5, "rgba(255,246,248,0.22)");
  feet.addColorStop(1, "rgba(255,246,248,0)");
  g.fillStyle = feet;
  g.fillRect(0, hz - H * 0.03, W, H * 0.09);
  const meadow = g.createLinearGradient(0, hz + H * 0.03, 0, H);
  meadow.addColorStop(0, "#B9D6A2");
  meadow.addColorStop(0.5, "#C9DFA8");
  meadow.addColorStop(1, "#D8E6B6");
  g.fillStyle = meadow;
  g.beginPath();
  g.moveTo(0, hz + H * 0.06);
  g.bezierCurveTo(W * 0.3, hz + H * 0.035, W * 0.7, hz + H * 0.07, W, hz + H * 0.05);
  g.lineTo(W, H);
  g.lineTo(0, H);
  g.fill();
  for (let i = 0; i < 260; i++) {
    const y = rand(hz + H * 0.06, H);
    const k = (y - hz) / (H - hz);
    g.strokeStyle = `rgba(${rand(110, 150)},${rand(150, 180)},${rand(100, 130)},${0.12 + 0.2 * k})`;
    g.lineWidth = 0.6 + 1.6 * k;
    const x = rand(0, W);
    g.beginPath();
    g.moveTo(x, y);
    g.quadraticCurveTo(x + rand(-3, 3), y - 6 * k - 2, x + rand(-6, 6), y - 12 * k - 4);
    g.stroke();
  }
  const top = hz + H * 0.055;
  const path = g.createLinearGradient(0, top, 0, H);
  path.addColorStop(0, "rgba(246,228,206,0.55)");
  path.addColorStop(1, "rgba(250,236,214,0.95)");
  g.fillStyle = path;
  g.beginPath();
  g.moveTo(cx - W * 0.012, top);
  g.bezierCurveTo(cx - W * 0.03, top + (H - top) * 0.4, cx - W * 0.22, H * 0.92, cx - W * 0.3, H);
  g.lineTo(cx + W * 0.3, H);
  g.bezierCurveTo(cx + W * 0.2, H * 0.92, cx + W * 0.035, top + (H - top) * 0.4, cx + W * 0.012, top);
  g.fill();
  for (let i = 0; i < 70; i++) {
    g.fillStyle = `rgba(255,${rand(200, 240)},${rand(220, 245)},${rand(0.35, 0.7)})`;
    const y = rand(hz + H * 0.08, H);
    const x = rand(0, W);
    g.fillRect(x, y, 1.2 + 2 * ((y - hz) / H), 1.2);
  }
  return c;
}

// A soft, painterly bow: each band laid down as several feathered strokes.
export function paintBow(G, dpr) {
  const { W, H, cx, cy, R, bw } = G;
  const c = canvas(W * dpr, H * dpr);
  const g = c.getContext("2d");
  g.scale(dpr, dpr);
  const inner = R - BANDS.length * bw, outer = R + bw * 0.6;
  const gr = g.createRadialGradient(cx, cy, inner - bw * 0.6, cx, cy, outer);
  const at = (r) => (r - (inner - bw * 0.6)) / (outer - (inner - bw * 0.6));
  gr.addColorStop(0, "rgba(255,255,255,0)");
  [...BANDS].reverse().forEach((col, i) => gr.addColorStop(clamp01(at(inner + (i + 0.5) * bw)), col));
  gr.addColorStop(1, "rgba(255,255,255,0)");
  g.strokeStyle = gr;
  g.lineWidth = outer - inner + bw * 0.6;
  g.globalAlpha = 0.72;
  g.beginPath();
  g.arc(cx, cy, (outer + inner - bw * 0.6) / 2, Math.PI, 2 * Math.PI);
  g.stroke();
  const glow = g.createRadialGradient(cx, cy, inner * 0.6, cx, cy, inner);
  glow.addColorStop(0, "rgba(255,250,240,0)");
  glow.addColorStop(1, "rgba(255,250,240,0.22)");
  g.globalAlpha = 1;
  g.fillStyle = glow;
  g.beginPath();
  g.arc(cx, cy, inner, Math.PI, 2 * Math.PI);
  g.fill();
  return c;
}

// The same bow, visible only through falling streaks: the rainbow seen in the rain.
export function streakMask(bow, dpr) {
  const c = canvas(bow.width, bow.height);
  const g = c.getContext("2d");
  g.drawImage(bow, 0, 0);
  g.globalCompositeOperation = "destination-in";
  g.strokeStyle = "rgba(0,0,0,0.9)";
  g.lineCap = "round";
  for (let i = 0; i < 900; i++) {
    const x = Math.random() * c.width;
    const y = Math.random() * c.height;
    const len = (14 + Math.random() * 26) * dpr;
    g.lineWidth = (0.8 + Math.random()) * dpr;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x - len * 0.22, y + len);
    g.stroke();
  }
  return c;
}

export function paintClouds(W, H, dpr) {
  const make = (side) => {
    const c = canvas(W * 1.1 * dpr, H * 0.8 * dpr);
    const g = c.getContext("2d");
    g.scale(dpr, dpr);
    const w = W * 1.1, h = H * 0.8;
    for (let i = 0; i < 34; i++) {
      const x = rand(w * 0.2, w * 0.8);
      const y = rand(h * 0.3, h * 0.6);
      const r = Math.min(rand(0.12, 0.26) * Math.max(w, h), x / 1.6, (w - x) / 1.6, y / 0.7, (h - y) / 0.7);
      const gr = g.createRadialGradient(x, y, r * 0.1, x, y, r);
      const v = Math.round(rand(150, 190));
      gr.addColorStop(0, `rgba(${v},${v - 4},${v + 12},0.55)`);
      gr.addColorStop(1, `rgba(${v},${v - 4},${v + 12},0)`);
      g.fillStyle = gr;
      g.save();
      g.translate(x, y);
      g.scale(1.6, 0.7);
      g.beginPath();
      g.arc(0, 0, r, 0, Math.PI * 2);
      g.restore();
      g.fill();
    }
    return c;
  };
  return { left: make(-1), right: make(1) };
}

export function makeDrops(n) {
  return Array.from({ length: n }, () => ({ x: Math.random() * 1.1, y: Math.random(), len: rand(12, 28), sp: rand(0.8, 1.4), still: Math.random() }));
}

// Draws the rain; drops crossing the bow catch its colour once the sun is out.
export function drawRain(g, G, drops, { amount, speed, glint, bow, move }) {
  const { W, H } = G;
  const { cx, cy, R, bw } = bow;
  const n = Math.floor(drops.length * amount);
  const bins = BANDS.map(() => []);
  const grey = [];
  for (let i = 0; i < n; i++) {
    const d = drops[i];
    if (move) {
      d.y += 0.016 * d.sp * speed;
      d.x -= 0.0035 * d.sp * speed;
      if (d.y > 1.05) (d.y = -0.06), (d.x = Math.random() * 1.1);
    }
    const x = d.x * W, y = d.y * H;
    const rr = Math.hypot(x - cx, y - cy);
    const band = y < cy ? Math.floor((R + bw / 2 - rr) / bw) : -1;
    (glint > 0 && band >= 0 && band < BANDS.length ? bins[band] : grey).push(x, y, d.len * (0.6 + 0.4 * speed));
  }
  const stroke = (list, style, width) => {
    if (!list.length) return;
    g.strokeStyle = style;
    g.lineWidth = width;
    g.beginPath();
    for (let i = 0; i < list.length; i += 3) {
      g.moveTo(list[i], list[i + 1]);
      g.lineTo(list[i] - list[i + 2] * 0.22, list[i + 1] + list[i + 2]);
    }
    g.stroke();
  };
  g.lineCap = "round";
  stroke(grey, `rgba(${Math.round(118 + 80 * glint)},${Math.round(116 + 70 * glint)},${Math.round(140 + 60 * glint)},${0.34 + 0.1 * glint})`, 1);
  g.globalAlpha = Math.min(1, 0.25 + glint);
  bins.forEach((list, i) => stroke(list, BANDS[i], 1.6));
  g.globalAlpha = 1;
}

export function drawSun(g, G, k, rays, t) {
  if (k <= 0) return;
  const { x, y } = G.sun;
  const s = Math.min(G.W, G.H);
  const halo = g.createRadialGradient(x, y, 0, x, y, s * 0.55);
  halo.addColorStop(0, `rgba(255,236,190,${0.75 * k})`);
  halo.addColorStop(0.25, `rgba(255,214,170,${0.32 * k})`);
  halo.addColorStop(1, "rgba(255,214,170,0)");
  g.fillStyle = halo;
  g.fillRect(0, 0, G.W, G.H);
  if (rays > 0) {
    g.save();
    g.globalCompositeOperation = "lighter";
    g.translate(x, y);
    const L = Math.hypot(G.W, G.H);
    for (let i = 0; i < 6; i++) {
      const a = Math.PI * 0.58 + i * 0.17 + Math.sin(t / 2600 + i) * 0.03 - (1 - rays) * 0.5;
      const w = 0.05 + (i % 3) * 0.02;
      const gr = g.createLinearGradient(0, 0, Math.cos(a) * L * 0.8, Math.sin(a) * L * 0.8);
      gr.addColorStop(0, `rgba(255,232,186,${0.06 * rays * k})`);
      gr.addColorStop(1, "rgba(255,226,170,0)");
      g.fillStyle = gr;
      g.beginPath();
      g.moveTo(0, 0);
      g.lineTo(Math.cos(a - w) * L, Math.sin(a - w) * L);
      g.lineTo(Math.cos(a + w) * L, Math.sin(a + w) * L);
      g.fill();
    }
    g.restore();
  }
  const disc = g.createRadialGradient(x, y, 0, x, y, s * 0.06);
  disc.addColorStop(0, `rgba(255,250,232,${k})`);
  disc.addColorStop(0.6, `rgba(255,236,196,${0.9 * k})`);
  disc.addColorStop(1, "rgba(255,226,180,0)");
  g.fillStyle = disc;
  g.fillRect(x - s * 0.07, y - s * 0.07, s * 0.14, s * 0.14);
}

export function drawSky(g, G, clear) {
  const sky = g.createLinearGradient(0, 0, 0, G.hz);
  const mix = (a, b) => a.map((v, i) => Math.round(v + (b[i] - v) * clear));
  const top = mix([176, 176, 192], [196, 214, 238]);
  const mid = mix([204, 200, 212], [244, 222, 226]);
  const low = mix([214, 208, 214], [252, 234, 214]);
  sky.addColorStop(0, `rgb(${top})`);
  sky.addColorStop(0.6, `rgb(${mid})`);
  sky.addColorStop(1, `rgb(${low})`);
  g.fillStyle = sky;
  g.fillRect(0, 0, G.W, G.H);
}

export function drawMist(g, G, k) {
  const { W, H, hz } = G;
  const m = g.createLinearGradient(0, hz - H * 0.08, 0, hz + H * 0.12);
  m.addColorStop(0, "rgba(255,248,250,0)");
  m.addColorStop(0.5, `rgba(255,248,250,${0.5 * k})`);
  m.addColorStop(1, "rgba(255,248,250,0)");
  g.fillStyle = m;
  g.fillRect(0, hz - H * 0.08, W, H * 0.2);
}
